import type { CategoryCodeData } from '~/apis/data/type'

// GA 이벤트 추적을 위한 Analytics 컴포저블
let analyticsComposable: any = null
let appStore: any = null

// 클라이언트 사이드에서만 Analytics 초기화
const initializeAnalytics = () => {
  if (process.client && !analyticsComposable) {
    try {
      analyticsComposable = useAnalytics()
      appStore = useAppStore()
    } catch (error) {
      console.warn('Analytics initialization failed:', error)
    }
  }
}

// GA 이벤트 추적 헬퍼 함수
const trackStoreEvent = (eventName: string, eventData: any) => {
  try {
    if (analyticsComposable?.trackEvent && appStore?.user) {
      analyticsComposable.trackEvent(eventName, {
        ...eventData,
        um_idx: appStore.user.UM_IDX ?? '',
        timestamp: new Date().toISOString()
      })
    }
  } catch (error) {
    console.warn('GA event tracking failed:', error)
  }
}

export interface SelectedCardInfo {
  gubunId: string
  gubunName: string
  item: CategoryCodeData
}

export interface SessionData {
  key: string
  path: 'basic' | 'advanced' | 'transform' | 'quick'
  selectedItems: Record<string, string[]>
  selectedCards: SelectedCardInfo[]
  selectedCount: number
  totalDrops: number
  inputData?: any // 각 화면에서 입력한 데이터
  createdAt: number
}

export interface DataStoreState {
  selectedItems: Record<string, string[]> // gubun_id별 선택된 sub_id 배열
  selectedCards: SelectedCardInfo[] // 선택된 카드의 상세 정보
  selectedCount: number
  totalDrops: number
  currentCategory: string // 현재 선택된 카테고리 (기본학습자료, 실전유형문제, 지문변형문제)
  groupedData: Record<string, CategoryCodeData[]> // gubun_id별 그룹 데이터
  gubunNames: Record<string, string> // gubun_id별 gubun_name
  sessions: Record<string, SessionData> // 키별 세션 데이터 관리
  currentSessionKey: string | null // 현재 활성 세션 키
}

export const useDataStore = defineStore('data', {
  state: (): DataStoreState => ({
    selectedItems: {},
    selectedCards: [],
    selectedCount: 0,
    totalDrops: 0,
    currentCategory: '기본학습자료',
    groupedData: {},
    gubunNames: {},
    sessions: {},
    currentSessionKey: null,
  }),

  getters: {
    /**
     * 선택된 카드들을 gubun_id별로 그룹핑하여 반환
     */
    selectedCardsByGroup: (state) => {
      const grouped: Record<string, SelectedCardInfo[]> = {}
      state.selectedCards.forEach((card) => {
        if (!grouped[card.gubunId]) {
          grouped[card.gubunId] = []
        }
        grouped[card.gubunId].push(card)
      })
      return grouped
    },

    /**
     * 특정 gubun_id의 선택된 카드 개수 반환
     */
    getSelectedCountByGroup: (state) => (gubunId: string) => {
      return state.selectedItems[gubunId]?.length || 0
    },

    /**
     * 특정 gubun_id의 총 Drops 점수 반환
     */
    getTotalDropsByGroup: (state) => (gubunId: string) => {
      return state.selectedCards
        .filter((card) => card.gubunId === gubunId)
        .reduce((total, card) => total + card.item.drops_point, 0)
    },
  },

  actions: {
    /**
     * 그룹 데이터 설정
     */
    setGroupedData(
      groupedData: Record<string, CategoryCodeData[]>,
      gubunNames: Record<string, string>,
    ) {
      this.groupedData = groupedData
      this.gubunNames = gubunNames
    },

    /**
     * 현재 카테고리 설정
     */
    setCurrentCategory(category: string) {
      this.currentCategory = category
    },

    /**
     * 개별 항목 선택/해제
     */
    toggleItem(gubunId: string, itemId: string) {
      if (!this.selectedItems[gubunId]) {
        this.selectedItems[gubunId] = []
      }

      const index = this.selectedItems[gubunId].indexOf(itemId)
      const item = this.groupedData[gubunId]?.find((i) => i.sub_id === itemId)

      if (index > -1) {
        // 선택 해제
        this.selectedItems[gubunId].splice(index, 1)
        // selectedCards에서도 제거
        this.selectedCards = this.selectedCards.filter(
          (card) => !(card.gubunId === gubunId && card.item.sub_id === itemId),
        )
      } else if (item) {
        // 선택 추가
        this.selectedItems[gubunId].push(itemId)
        // selectedCards에도 추가
        this.selectedCards.push({
          gubunId,
          gubunName: this.gubunNames[gubunId] || `그룹 ${gubunId}`,
          item,
        })
      }

      this.updateCounts()
    },

    /**
     * 그룹 전체 선택/해제
     */
    toggleAllGroup(gubunId: string) {
      const group = this.groupedData[gubunId]
      if (!group) return

      const isAllSelected = group.every((item) =>
        this.selectedItems[gubunId]?.includes(item.sub_id),
      )

      if (isAllSelected) {
        // 전체 해제
        this.selectedItems[gubunId] = []
        // selectedCards에서도 해당 그룹 모두 제거
        this.selectedCards = this.selectedCards.filter(
          (card) => card.gubunId !== gubunId,
        )
      } else {
        // 전체 선택
        this.selectedItems[gubunId] = group.map((item) => item.sub_id)
        // selectedCards에서 기존 그룹 제거 후 새로 추가
        this.selectedCards = this.selectedCards.filter(
          (card) => card.gubunId !== gubunId,
        )
        const gubunName = this.gubunNames[gubunId] || `그룹 ${gubunId}`
        group.forEach((item) => {
          this.selectedCards.push({
            gubunId,
            gubunName,
            item,
          })
        })
      }

      this.updateCounts()
    },

    /**
     * 선택 상태 초기화
     */
    clearSelections() {
      this.selectedItems = {}
      this.selectedCards = []
      this.selectedCount = 0
      this.totalDrops = 0
    },

    /**
     * 특정 그룹의 선택 상태 초기화
     */
    clearGroupSelection(gubunId: string) {
      this.selectedItems[gubunId] = []
      this.selectedCards = this.selectedCards.filter(
        (card) => card.gubunId !== gubunId,
      )
      this.updateCounts()
    },

    /**
     * 카운트 업데이트
     */
    updateCounts() {
      let count = 0
      let drops = 0

      Object.keys(this.selectedItems).forEach((gubunId) => {
        const group = this.groupedData[gubunId]
        if (!group) return

        this.selectedItems[gubunId].forEach((itemId) => {
          const item = group.find((i) => i.sub_id === itemId)
          if (item) {
            count++
            drops += item.drops_point
          }
        })
      })

      this.selectedCount = count
      this.totalDrops = drops
    },

    /**
     * 선택된 항목이 있는지 확인
     */
    isItemSelected(gubunId: string, itemId: string): boolean {
      return this.selectedItems[gubunId]?.includes(itemId) || false
    },

    /**
     * 그룹이 전체 선택되었는지 확인
     */
    isGroupFullySelected(gubunId: string): boolean {
      const group = this.groupedData[gubunId]
      if (!group || group.length === 0) return false

      return group.every((item) =>
        this.selectedItems[gubunId]?.includes(item.sub_id),
      )
    },

    /**
     * 세션 키와 경로 저장
     */
    setSessionKey(
      key: string,
      path: 'basic' | 'advanced' | 'transform' | 'quick',
    ) {
      initializeAnalytics()

      this.currentSessionKey = key
      this.sessions[key] = {
        key,
        path,
        selectedItems: { ...this.selectedItems },
        selectedCards: [...this.selectedCards],
        selectedCount: this.selectedCount,
        totalDrops: this.totalDrops,
        createdAt: Date.now(),
      }

      // GA 이벤트: 새 세션 생성
      trackStoreEvent('data_session_created', {
        event_category: 'session_management',
        session_key: key,
        target_path: path,
        selection_count: this.selectedCount,
        total_drops: this.totalDrops,
        selected_groups_count: Object.keys(this.selectedItems).length,
        current_category: this.currentCategory,
      })
    },

    /**
     * 세션 데이터 가져오기
     */
    getSession(key: string): SessionData | null {
      return this.sessions[key] || null
    },

    /**
     * 세션 입력 데이터 업데이트
     */
    updateSessionInputData(key: string, inputData: any) {
      if (this.sessions[key]) {
        this.sessions[key].inputData = inputData
      }
    },

    /**
     * 현재 세션 가져오기
     */
    getCurrentSession(): SessionData | null {
      if (!this.currentSessionKey) return null
      return this.sessions[this.currentSessionKey] || null
    },

    /**
     * 세션 삭제
     */
    deleteSession(key: string) {
      const sessionData = this.sessions[key]

      delete this.sessions[key]
      if (this.currentSessionKey === key) {
        this.currentSessionKey = null
      }

      // GA 이벤트: 세션 삭제
      if (sessionData) {
        trackStoreEvent('data_session_deleted', {
          event_category: 'session_management',
          session_key: key,
          session_path: sessionData.path,
          session_age_minutes: Math.round(
            (Date.now() - sessionData.createdAt) / 60000,
          ),
          had_selections: sessionData.selectedCount > 0,
          selection_count: sessionData.selectedCount,
          was_current: this.currentSessionKey === key,
        })
      }
    },

    /**
     * 세션으로부터 선택 상태 복원
     */
    restoreSessionSelections(sessionKey: string) {
      const session = this.sessions[sessionKey]
      if (!session) {
        // GA 이벤트: 세션 복원 실패
        trackStoreEvent('data_session_restore_failed', {
          event_category: 'session_management',
          session_key: sessionKey,
          reason: 'session_not_found',
          available_sessions: Object.keys(this.sessions).length,
        })
        return false
      }

      const previousCount = this.selectedCount

      // 세션 데이터로부터 선택 상태 복원
      this.selectedItems = { ...session.selectedItems }
      this.selectedCards = [...session.selectedCards]
      this.selectedCount = session.selectedCount
      this.totalDrops = session.totalDrops

      // GA 이벤트: 세션 복원 성공
      trackStoreEvent('data_session_restored', {
        event_category: 'session_management',
        session_key: sessionKey,
        target_path: session.path,
        restored_count: session.selectedCount,
        restored_drops: session.totalDrops,
        previous_count: previousCount,
        session_age_minutes: Math.round(
          (Date.now() - session.createdAt) / 60000,
        ),
        restored_groups_count: Object.keys(session.selectedItems).length,
      })

      return true
    },

    /**
     * 오래된 세션 정리 (24시간 이상)
     */
    cleanOldSessions() {
      const now = Date.now()
      const oneDay = 24 * 60 * 60 * 1000
      const oldSessions: string[] = []

      Object.keys(this.sessions).forEach((key) => {
        if (now - this.sessions[key].createdAt > oneDay) {
          oldSessions.push(key)
        }
      })

      if (oldSessions.length > 0) {
        // GA 이벤트: 오래된 세션 정리
        trackStoreEvent('data_sessions_cleaned', {
          event_category: 'session_management',
          cleaned_count: oldSessions.length,
          total_sessions_before: Object.keys(this.sessions).length,
          cleanup_reason: 'expired_24h',
        })

        oldSessions.forEach((key) => {
          this.deleteSession(key)
        })
      }
    },

    /**
     * 세션 데이터 직접 설정 (임시저장 복원용)
     */
    setSession(
      key: string,
      sessionData: Partial<SessionData> & {
        path: 'basic' | 'advanced' | 'transform' | 'quick'
      },
    ) {
      initializeAnalytics()

      this.sessions[key] = {
        key,
        path: sessionData.path,
        selectedItems: sessionData.selectedItems || {},
        selectedCards: sessionData.selectedCards || [],
        selectedCount: sessionData.selectedCount || 0,
        totalDrops: sessionData.totalDrops || 0,
        inputData: sessionData.inputData,
        createdAt: sessionData.createdAt || Date.now(),
      }

      this.currentSessionKey = key

      // GA 이벤트: 세션 직접 생성
      trackStoreEvent('data_session_set_directly', {
        event_category: 'session_management',
        session_key: key,
        target_path: sessionData.path,
        selection_count: sessionData.selectedCount || 0,
        total_drops: sessionData.totalDrops || 0,
        source: 'temp_restore',
      })
    },
  },

  persist: {
    key: 'data',
    storage: persistedState.sessionStorage,
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDataStore, import.meta.hot))
}
